/**
 * 데이터 접근 권한 설정 페이지
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Form, Table, Badge, Alert, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { adminService } from '../../services/adminService';
import { dataAccessPermissionService } from '../../services/dataAccessPermissionService';
import { DataAccessPermission, DataAccessPermissionRequest, AdminUser } from '../../types/admin';

const DataAccessPermissions: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<DataAccessPermissionRequest>({
    admin_id: 0,
    access_type: 'all',
    restrictions: {
      can_view_revenue: true,
      can_view_customer_info: true,
      can_modify_data: false,
    },
  });

  const { data: adminsData } = useQuery({
    queryKey: ['admins'],
    queryFn: () => adminService.getAdmins({}),
  });

  const { data: permissions } = useQuery({
    queryKey: ['dataAccessPermissions'],
    queryFn: () => dataAccessPermissionService.getDataAccessPermissions(),
  });

  const saveMutation = useMutation({
    mutationFn: (data: DataAccessPermissionRequest) =>
      dataAccessPermissionService.setDataAccessPermission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataAccessPermissions'] });
      setShowModal(false);
      alert('데이터 접근 권한이 저장되었습니다.');
    },
    onError: (error: any) => {
      alert(`저장 실패: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DataAccessPermissionRequest> }) =>
      dataAccessPermissionService.updateDataAccessPermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataAccessPermissions'] });
      setShowModal(false);
      alert('데이터 접근 권한이 수정되었습니다.');
    },
    onError: (error: any) => {
      alert(`수정 실패: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => dataAccessPermissionService.deleteDataAccessPermission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataAccessPermissions'] });
      alert('데이터 접근 권한이 삭제되었습니다.');
    },
    onError: (error: any) => {
      alert(`삭제 실패: ${error.message}`);
    },
  });

  const handleOpenModal = (adminId?: number, permission?: DataAccessPermission) => {
    if (permission) {
      setFormData({
        admin_id: permission.admin_id,
        access_type: permission.access_type,
        hall_ids: permission.hall_ids,
        date_from: permission.date_from,
        date_to: permission.date_to,
        department: permission.department,
        restrictions: permission.restrictions,
      });
    } else if (adminId) {
      setFormData({
        admin_id: adminId,
        access_type: 'all',
        restrictions: {
          can_view_revenue: true,
          can_view_customer_info: true,
          can_modify_data: false,
        },
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      admin_id: 0,
      access_type: 'all',
      restrictions: {
        can_view_revenue: true,
        can_view_customer_info: true,
        can_modify_data: false,
      },
    });
  };

  const handleSave = () => {
    if (!formData.admin_id) {
      alert('관리자를 선택해주세요.');
      return;
    }

    const existingPermission = permissions?.find((p) => p.admin_id === formData.admin_id);
    if (existingPermission) {
      updateMutation.mutate({ id: existingPermission.id, data: formData });
    } else {
      saveMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  const getAccessTypeLabel = (type: string) => {
    switch (type) {
      case 'all':
        return '전체 접근';
      case 'hall':
        return '관별 제한';
      case 'date_range':
        return '기간 제한';
      case 'department':
        return '부서별 제한';
      default:
        return type;
    }
  };

  const getAdminName = (adminId: number): string => {
    const admin = adminsData?.admins.find((a) => a.id === adminId);
    return admin?.name || `ID: ${adminId}`;
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>데이터 접근 권한 설정</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/admins')} className="me-2">
              관리자 목록
            </Button>
            <Button variant="primary" onClick={() => handleOpenModal()}>
              권한 추가
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <Table striped bordered hover variant="dark">
              <thead>
                <tr>
                  <th>관리자</th>
                  <th>접근 유형</th>
                  <th>제한 사항</th>
                  <th>제한 옵션</th>
                  <th style={{ width: '150px' }}>작업</th>
                </tr>
              </thead>
              <tbody>
                {permissions && permissions.length > 0 ? (
                  permissions.map((permission) => (
                    <tr key={permission.id}>
                      <td>{getAdminName(permission.admin_id)}</td>
                      <td>
                        <Badge bg="info">{getAccessTypeLabel(permission.access_type)}</Badge>
                      </td>
                      <td>
                        {permission.access_type === 'hall' && permission.hall_ids && (
                          <div>관 ID: {permission.hall_ids.join(', ')}</div>
                        )}
                        {permission.access_type === 'date_range' && (
                          <div>
                            {permission.date_from} ~ {permission.date_to}
                          </div>
                        )}
                        {permission.access_type === 'department' && permission.department && (
                          <div>{permission.department}</div>
                        )}
                        {permission.access_type === 'all' && <div>-</div>}
                      </td>
                      <td>
                        {permission.restrictions && (
                          <div style={{ fontSize: '0.9em' }}>
                            {permission.restrictions.can_view_revenue && (
                              <Badge bg="success" className="me-1">매출 조회</Badge>
                            )}
                            {permission.restrictions.can_view_customer_info && (
                              <Badge bg="success" className="me-1">고객정보 조회</Badge>
                            )}
                            {permission.restrictions.can_modify_data && (
                              <Badge bg="warning" className="me-1">데이터 수정</Badge>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenModal(permission.admin_id, permission)}
                          className="me-1"
                        >
                          수정
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(permission.id)}
                        >
                          삭제
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">
                      설정된 데이터 접근 권한이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* 권한 설정 모달 */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>데이터 접근 권한 설정</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>관리자 선택</Form.Label>
                <Form.Select
                  value={formData.admin_id}
                  onChange={(e) =>
                    setFormData({ ...formData, admin_id: parseInt(e.target.value) })
                  }
                  disabled={!!permissions?.find((p) => p.admin_id === formData.admin_id)}
                >
                  <option value="0">관리자를 선택하세요</option>
                  {adminsData?.admins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name} ({admin.email})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>접근 유형</Form.Label>
                <Form.Select
                  value={formData.access_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      access_type: e.target.value as 'all' | 'hall' | 'date_range' | 'department',
                    })
                  }
                >
                  <option value="all">전체 접근</option>
                  <option value="hall">관별 제한</option>
                  <option value="date_range">기간 제한</option>
                  <option value="department">부서별 제한</option>
                </Form.Select>
              </Form.Group>

              {formData.access_type === 'hall' && (
                <Form.Group className="mb-3">
                  <Form.Label>관 ID (쉼표로 구분)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="예: 1, 2, 3"
                    value={formData.hall_ids?.join(', ') || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hall_ids: e.target.value
                          .split(',')
                          .map((id) => parseInt(id.trim()))
                          .filter((id) => !isNaN(id)),
                      })
                    }
                  />
                </Form.Group>
              )}

              {formData.access_type === 'date_range' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>시작 날짜</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.date_from || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, date_from: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>종료 날짜</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.date_to || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, date_to: e.target.value })
                      }
                    />
                  </Form.Group>
                </>
              )}

              {formData.access_type === 'department' && (
                <Form.Group className="mb-3">
                  <Form.Label>부서명</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="예: 영업팀, 마케팅팀"
                    value={formData.department || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>권한 옵션</Form.Label>
                <div>
                  <Form.Check
                    type="checkbox"
                    label="매출 정보 조회 가능"
                    checked={formData.restrictions?.can_view_revenue || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        restrictions: {
                          ...formData.restrictions,
                          can_view_revenue: e.target.checked,
                        },
                      })
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    label="고객 정보 조회 가능"
                    checked={formData.restrictions?.can_view_customer_info || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        restrictions: {
                          ...formData.restrictions,
                          can_view_customer_info: e.target.checked,
                        },
                      })
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    label="데이터 수정 가능"
                    checked={formData.restrictions?.can_modify_data || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        restrictions: {
                          ...formData.restrictions,
                          can_modify_data: e.target.checked,
                        },
                      })
                    }
                  />
                </div>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saveMutation.isPending || updateMutation.isPending}
            >
              {saveMutation.isPending || updateMutation.isPending ? '저장 중...' : '저장'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default DataAccessPermissions;

